import { SerializedStyles } from "@emotion/react";
import { Dispatch, Ref, SetStateAction, memo, useCallback } from "react";
import { useCheckUnderwriting, usePartialSubmit } from "src/apps/AppFlow/pages/hooks";
import { DropdownSelector, FormFlowPage } from "src/components";
import { PropertyTypeValue, PropertyTypes } from "src/types/Application";

import { RejectedReason, enums, formatters } from "@fraction/shared";

import condoIcon from "src/images/illustrative-buttons/condo-icon.svg?react";
import coopIcon from "src/images/illustrative-buttons/coop-icon.svg?react";
import farmIcon from "src/images/illustrative-buttons/farm-icon.svg?react";
import investmentPropertyIcon from "src/images/illustrative-buttons/investment-property-icon.svg?react";
import leaseholdIcon from "src/images/illustrative-buttons/leasehold-icon.svg?react";
import mobileIcon from "src/images/illustrative-buttons/mobile-icon.svg?react";
import overFourUnitsIcon from "src/images/illustrative-buttons/over-four-units-icon.svg?react";
import primaryResidenceIcon from "src/images/illustrative-buttons/primary-residence-icon.svg?react";
import secondaryResidenceIcon from "src/images/illustrative-buttons/secondary-residence-icon.svg?react";
import singleFamilyIcon from "src/images/illustrative-buttons/single-family-icon.svg?react";
import townhouseIcon from "src/images/illustrative-buttons/townhome-icon.svg?react";
import twoToFourUnitsIcon from "src/images/illustrative-buttons/two-to-four-units-icon.svg?react";

const { PROPERTY_TYPE_MAP, FULL_PROPERTY_TYPE_MAP } = formatters.enums;
export const PROPERTY_TYPE_OPTIONS = [
  {
    icon: townhouseIcon,
    label: FULL_PROPERTY_TYPE_MAP[enums.PropertyType.TOWNHOME],
    value: enums.PropertyType.TOWNHOME,
  },
  {
    icon: singleFamilyIcon,
    label: PROPERTY_TYPE_MAP[enums.PropertyType.SINGLE_FAMILY],
    value: enums.PropertyType.SINGLE_FAMILY,
  },
  { icon: condoIcon, label: PROPERTY_TYPE_MAP[enums.PropertyType.CONDO], value: enums.PropertyType.CONDO },
  {
    icon: leaseholdIcon,
    label: PROPERTY_TYPE_MAP[enums.PropertyType.LEASEHOLD],
    value: enums.PropertyType.LEASEHOLD,
  },
  { icon: mobileIcon, label: PROPERTY_TYPE_MAP[enums.PropertyType.MOBILE], value: enums.PropertyType.MOBILE },
  { icon: coopIcon, label: PROPERTY_TYPE_MAP[enums.PropertyType.COOP], value: enums.PropertyType.COOP },
  { icon: farmIcon, label: PROPERTY_TYPE_MAP[enums.PropertyType.FARM], value: enums.PropertyType.FARM },
  {
    icon: twoToFourUnitsIcon,
    label: PROPERTY_TYPE_MAP[enums.PropertyType.TWO_TO_FOUR_UNITS],
    value: enums.PropertyType.TWO_TO_FOUR_UNITS,
  },
  {
    icon: overFourUnitsIcon,
    label: PROPERTY_TYPE_MAP[enums.PropertyType.OVER_FOUR_UNITS],
    value: enums.PropertyType.OVER_FOUR_UNITS,
  },
];

const PROPERTY_USAGE_OPTIONS = [
  {
    value: enums.PropertyUsage.PRIMARY_RESIDENCE,
    label: formatters.enums.PROPERTY_USAGE_MAP[enums.PropertyUsage.PRIMARY_RESIDENCE],
    icon: primaryResidenceIcon,
  },
  {
    value: enums.PropertyUsage.SECONDARY_RESIDENCE,
    label: formatters.enums.PROPERTY_USAGE_MAP[enums.PropertyUsage.SECONDARY_RESIDENCE],
    icon: secondaryResidenceIcon,
  },
  {
    value: enums.PropertyUsage.INVESTMENT,
    label: formatters.enums.PROPERTY_USAGE_MAP[enums.PropertyUsage.INVESTMENT],
    icon: investmentPropertyIcon,
  },
];

export interface PropertyTypeProps {
  onChange: Dispatch<SetStateAction<PropertyTypeValue | undefined>>;
  value?: PropertyTypeValue;
  propertyAddress?: string;

  focusRef?: Ref<HTMLInputElement>;
  onNext: () => void;
  onBack: () => void;
  style?: SerializedStyles;
}

const PropertyType = ({
  onNext,
  onBack,
  style,
  focusRef,
  value,
  onChange,
  propertyAddress,
  ...props
}: PropertyTypeProps) => {
  const validate = useCallback(
    () => value?.propertyType !== undefined && value?.propertyUsage !== undefined,
    [value]
  );

  const typestring = value?.propertyType
    ? FULL_PROPERTY_TYPE_MAP[value?.propertyType]?.toLowerCase()
    : "unaccepted" || "unaccepted";

  const runUnderwriting = useCheckUnderwriting({
    failMessage: (result) =>
      result?.message?.includes("usage")
        ? "Unfortunately, Fraction only lends on primary residences."
        : `Unfortunately, Fraction does not lend on ${
            typestring ? `${typestring} properties` : "your property type"
          }.`,
    getSpecificUnderwritingResult: (result) =>
      result?.propertyProperties?.[enums.UnderwritingRuleName.PROPERTY_TYPE_CHECK]?.status ===
      enums.EngineRuleStatus.FAILED
        ? result?.propertyProperties?.[enums.UnderwritingRuleName.PROPERTY_TYPE_CHECK]
        : result?.propertyProperties?.[enums.UnderwritingRuleName.PROPERTY_USAGE_CHECK],
    onNext,
    onChange,
  });

  const handleChangePropertyType = useCallback(
    (propertyType: PropertyTypes) => {
      runUnderwriting.onChange?.((prev: PropertyTypeValue) => ({ ...prev, ...value, propertyType }));
    },
    [runUnderwriting.onChange, value]
  );

  const handleChangePropertyUsage = useCallback(
    (propertyUsage: enums.PropertyUsage) => {
      runUnderwriting.onChange?.((prev: PropertyTypeValue) => ({ ...prev, ...value, propertyUsage }));
    },
    [runUnderwriting.onChange, value]
  );

  const { handleNext, nextDisabled, nextLoading } = usePartialSubmit({
    onNext: runUnderwriting.mutateAsync,
    validate,
    reasonsToBlock: [RejectedReason.PROPERTY_TYPE],
  });

  return (
    <FormFlowPage
      {...props}
      title={`What type of property is ${propertyAddress || "it"}?`}
      description="Fraction works with detached homes, condos, townhomes and more."
      onNext={handleNext}
      onBack={onBack}
      style={style}
      nextDisabled={runUnderwriting.nextDisabled || nextDisabled}
      nextLoading={runUnderwriting.isPending || nextLoading}
    >
      <DropdownSelector
        label="What sort of property is it?"
        focusRef={focusRef}
        value={value?.propertyType}
        onChange={handleChangePropertyType}
        options={PROPERTY_TYPE_OPTIONS}
      />
      <DropdownSelector
        label="How do you use the property?"
        value={value?.propertyUsage}
        onChange={handleChangePropertyUsage}
        options={PROPERTY_USAGE_OPTIONS}
      />
    </FormFlowPage>
  );
};

export default memo(PropertyType);
